import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import logo from "../../assets/logo.png";
import profilePlaceHolder from "../../assets/profile-placeholder.png";
import { isOwnerOrFinance } from "../../utils/role-check";
import LanguageSwitcher from "../LanguageSwitcher/LanguageSwitcher";
import "./navigation.style.css";
function NavigationView({ auth, user, picLocation, handleLogout, isVoormanOnAnyProject }) {
    const { t } = useTranslation();
    const location = useLocation();
    const [menuOpen, setMenuOpen] = useState(false);

    const profilePic = picLocation || profilePlaceHolder;

    const handleMenuToggle = () => setMenuOpen((open) => !open);
    const handleLinkClick = () => setMenuOpen(false);

    // Lock body scroll when menu is open
    useEffect(() => {
        if (menuOpen) {
            document.body.classList.add('menu-open');
        } else {
            document.body.classList.remove('menu-open');
        }

        // Cleanup on unmount
        return () => document.body.classList.remove('menu-open');
    }, [menuOpen]);

    // Close menu on route change
    useEffect(() => {
        setMenuOpen(false);
    }, [location.pathname]);

    if (!auth.isAuthenticated) return null;

    return (
        <nav className="navigation" role="navigation" aria-label="Main navigation">
            {/* Overlay for mobile menu */}
            <div
                className={`nav-overlay${menuOpen ? " open" : ""}`}
                onClick={() => setMenuOpen(false)}
                aria-hidden="true"
            />

            <div className="nav-container">
                {/* Logo - now points to dashboard */}
                <Link
                    to="/dashboard"
                    className="nav-logo"
                    onClick={handleLinkClick}
                    aria-label="Ferreira Systeembouw - Ga naar dashboard"
                >
                    <img
                        src={logo}
                        alt="Ferreira Systeembouw Logo"
                        loading="eager"
                    />
                </Link>

                {/* Hamburger icon */}
                <button
                    type="button"
                    className={`hamburger${menuOpen ? " open" : ""}`}
                    onClick={handleMenuToggle}
                    aria-label={menuOpen ? "Sluit menu" : "Open menu"}
                    aria-expanded={menuOpen}
                    aria-controls="main-navigation-menu"
                >
                    <span className="bar" aria-hidden="true"></span>
                    <span className="bar" aria-hidden="true"></span>
                    <span className="bar" aria-hidden="true"></span>
                </button>

                {/* Navigatie Links */}
                <ul
                    id="main-navigation-menu"
                    className={`nav-links${menuOpen ? " open" : ""}`}
                    role="menu"
                >
                    <li role="none">
                        <Link
                            to="/dashboard"
                            className={location.pathname.startsWith("/dashboard") ? "active" : ""}
                            onClick={handleLinkClick}
                            role="menuitem"
                            aria-current={location.pathname.startsWith("/dashboard") ? "page" : undefined}
                        >
                            {t('nav.dashboard')}
                        </Link>
                    </li>

                    {isOwnerOrFinance(auth.role) && (
                        <li role="none">
                            <Link
                                to="/agenda"
                                className={location.pathname.startsWith("/agenda") ? "active" : ""}
                                onClick={handleLinkClick}
                                role="menuitem"
                                aria-current={location.pathname.startsWith("/agenda") ? "page" : undefined}
                            >
                                {t('nav.agenda')}
                            </Link>
                        </li>
                    )}

                    <li role="none">
                        <Link
                            to="/projects"
                            className={location.pathname.startsWith("/projects") ? "active" : ""}
                            onClick={handleLinkClick}
                            role="menuitem"
                            aria-current={location.pathname.startsWith("/projects") ? "page" : undefined}
                        >
                            {t('nav.projects')}
                        </Link>
                    </li>

                    {auth.role !== "klant" && (
                        <li role="none">
                            <Link
                                to="/timesheets"
                                className={location.pathname.startsWith("/timesheets") ? "active" : ""}
                                onClick={handleLinkClick}
                                role="menuitem"
                                aria-current={location.pathname.startsWith("/timesheets") ? "page" : undefined}
                            >
                                {t('nav.timesheets')}
                            </Link>
                        </li>
                    )}

                    {(isOwnerOrFinance(auth.role) || isVoormanOnAnyProject) && (
                        <li role="none">
                            <Link
                                to="/users"
                                className={location.pathname.startsWith("/users") ? "active" : ""}
                                onClick={handleLinkClick}
                                role="menuitem"
                                aria-current={location.pathname.startsWith("/users") ? "page" : undefined}
                            >
                                {t('nav.users')}
                            </Link>
                        </li>
                    )}

                    {isOwnerOrFinance(auth.role) && (
                        <li role="none">
                            <Link
                                to="/customers"
                                className={location.pathname.startsWith("/customers") ? "active" : ""}
                                onClick={handleLinkClick}
                                role="menuitem"
                                aria-current={location.pathname.startsWith("/customers") ? "page" : undefined}
                            >
                                {t('nav.customers') || 'Klanten'}
                            </Link>
                        </li>
                    )}

                    {/* Language Switcher - dropdown on desktop, buttons on mobile */}
                    <li className="nav-language-switcher" role="none">
                        <LanguageSwitcher variant="dropdown" />
                        <LanguageSwitcher variant="default" />
                    </li>

                    <li className="nav-profile" role="none">
                        <Link
                            className="no-hover"
                            to="/profile"
                            onClick={handleLinkClick}
                            role="menuitem"
                            aria-label={`${t('nav.profile')} - ${user?.name || 'Gebruiker'}`}
                        >
                            <img
                                src={profilePic}
                                alt={user?.name || t('nav.profile')}
                                loading="lazy"
                            />
                        </Link>
                    </li>

                    <li role="none">
                        <button
                            type="button"
                            onClick={() => { handleLogout(); setMenuOpen(false); }}
                            className="btn btn-logout"
                            role="menuitem"
                            aria-label="Uitloggen"
                        >
                            {t('nav.logout')}
                        </button>
                    </li>
                </ul>

                {/* Close button for mobile menu */}
                {menuOpen && (
                    <button
                        type="button"
                        className="nav-close-btn btn btn-secondary"
                        onClick={handleLinkClick}
                        aria-label="Sluit menu"
                    >
                        <span>Menu</span>
                        <span className="close-icon" aria-hidden="true">✕</span>
                    </button>
                )}
            </div>
        </nav>
    );
}

NavigationView.propTypes = {
    auth: PropTypes.object.isRequired,
    user: PropTypes.object,
    picLocation: PropTypes.string,
    handleLogout: PropTypes.func.isRequired,
    isVoormanOnAnyProject: PropTypes.bool,
};

export default NavigationView;
